export const FLIGHT_ENDPOINT_TRIM_SECONDS = 1;

export type FlightEndpointTrimInfo = {
  original: number;
  leading: number;
  trailing: number;
  total: number;
  seconds: number;
};

type ElapsedFlightPoint = {
  elapsedSeconds?: unknown;
};

export function trimEndpointSamples<T extends ElapsedFlightPoint>(
  inputPoints: T[],
  seconds = FLIGHT_ENDPOINT_TRIM_SECONDS,
): { points: T[]; info: FlightEndpointTrimInfo } {
  const points = Array.isArray(inputPoints) ? inputPoints : [];
  const trimSeconds = Number.isFinite(seconds)
    ? Math.max(0, Number(seconds))
    : FLIGHT_ENDPOINT_TRIM_SECONDS;
  const baseInfo = {
    original: points.length,
    leading: 0,
    trailing: 0,
    total: 0,
    seconds: trimSeconds,
  };
  if (points.length < 4) return { points, info: baseInfo };

  const firstPoint = points.find((point) => Number.isFinite(point.elapsedSeconds));
  const lastPoint = [...points].reverse().find((point) => Number.isFinite(point.elapsedSeconds));
  const firstElapsed = firstPoint?.elapsedSeconds;
  const lastElapsed = lastPoint?.elapsedSeconds;
  if (
    !Number.isFinite(firstElapsed)
    || !Number.isFinite(lastElapsed)
    || Number(lastElapsed) - Number(firstElapsed) <= 10
  ) {
    return { points, info: baseInfo };
  }

  let startIndex = points.findIndex((point) => (
    Number.isFinite(point.elapsedSeconds)
    && Number(point.elapsedSeconds) - Number(firstElapsed) >= trimSeconds
  ));
  let endIndex = points.length - 1;
  while (endIndex >= 0) {
    const elapsed = points[endIndex]?.elapsedSeconds;
    if (Number.isFinite(elapsed) && Number(lastElapsed) - Number(elapsed) >= trimSeconds) break;
    endIndex -= 1;
  }

  if (startIndex < 0) startIndex = 0;
  if (startIndex > endIndex || endIndex - startIndex + 1 < 3) return { points, info: baseInfo };

  const trimmed = points.slice(startIndex, endIndex + 1);
  const info = {
    ...baseInfo,
    leading: startIndex,
    trailing: points.length - endIndex - 1,
    total: startIndex + points.length - endIndex - 1,
  };
  return { points: info.total ? trimmed : points, info };
}
